/**
 * The help centre (service-core /api/help, workspace docs/help-centre.md): the
 * articles a shop, a courier or a partner desk can read, in the backoffice's
 * language. One contract for the backoffice and TOU Field; which articles the
 * caller may see is the server's call.
 *
 * Every query key starts with ['help'].
 */
import { api } from './api'
import type { Persona } from './personas'
import { supportQueryRetry } from './support'

export type HelpSide = 'shop' | 'courier' | 'partner'

export const HELP_SIDES: HelpSide[] = ['shop', 'courier', 'partner']

export const HELP_SIDE_LABEL: Record<HelpSide, string> = {
  shop: 'Shop',
  courier: 'Courier',
  partner: 'Partner',
}

export interface HelpArticleSummary {
  slug: string
  title: string
  summary?: string | null
  order?: number | null
  /** Backoffice paths this article explains (the "?" button). */
  routes?: string[] | null
  fieldRoutes?: string[] | null
}

export interface HelpSection {
  name: string
  articles: HelpArticleSummary[]
}

export interface HelpArticleList {
  side: HelpSide
  lang: string
  sections: HelpSection[]
}

export interface HelpArticle {
  slug: string
  lang: string
  title: string
  section?: string | null
  summary?: string | null
  /** Markdown, front matter stripped (help-api-contract.md § Markdown). */
  body: string
  routes?: string[] | null
  fieldRoutes?: string[] | null
  updatedAt?: string | null
  related?: { slug: string; title: string }[] | null
}

export interface HelpSearchResult {
  slug: string
  title: string
  summary?: string | null
  snippet?: string | null
}

export interface HelpSearchResponse {
  lang: string
  results: HelpSearchResult[]
}

export const SUPPORT_EMAIL = 'support@comforthub.ee'

/** The first article a new shop should read; the dashboard's first-run nudge opens it. */
export const SHOP_FIRST_WEEK_SLUG = 'shop-getting-started'

/** Search asks the server from this many characters (the server answers [] below). */
export const HELP_SEARCH_MIN = 2

/**
 * The side a backoffice persona reads help as. Admin has no side of its own: it
 * previews one with the "View as" switch on the help pages (shop by default).
 */
export function helpSideForPersona(persona: Persona | 'None' | null | undefined): HelpSide | null {
  switch (persona) {
    case 'Merchant':
    case 'Manager':
    case 'Worker':
      return 'shop'
    case 'Courier':
      return 'courier'
    case 'Partner':
      return 'partner'
    default:
      return null
  }
}

// ── Calls ─────────────────────────────────────────────────────────────────

/** The backoffice is English-only; the server would otherwise follow the browser. */
const ENGLISH = { 'Accept-Language': 'en' }

export const helpKeys = {
  all: ['help'] as const,
  articles: (side: HelpSide) => ['help', 'articles', side] as const,
  article: (side: HelpSide, slug: string) => ['help', 'article', side, slug] as const,
  search: (side: HelpSide, q: string) => ['help', 'search', side, q] as const,
}

export const helpApi = {
  articles: async (side: HelpSide) =>
    (await api.get<HelpArticleList>('/help/articles', { params: { side }, headers: ENGLISH })).data,
  article: async (side: HelpSide, slug: string) =>
    (await api.get<HelpArticle>(`/help/articles/${encodeURIComponent(slug)}`, {
      params: { side },
      headers: ENGLISH,
    })).data,
  search: async (side: HelpSide, q: string) =>
    (await api.get<HelpSearchResponse>('/help/search', { params: { side, q }, headers: ENGLISH })).data,
}

/** The answers are cached for 5 minutes on the server too (Cache-Control max-age=300). */
export const HELP_STALE_MS = 5 * 60_000

/** 404 (a service-core without help) and 403 are answers: only a 5xx is asked again. */
export const helpQueryRetry = supportQueryRetry

/** True when the list answered in the contract's shape; anything else counts as no help. */
export function isHelpList(data: unknown): data is HelpArticleList {
  return Boolean(data) && Array.isArray((data as { sections?: unknown }).sections)
}

/** Every article of the list once, in the server's order. */
export function flattenArticles(list: HelpArticleList | null | undefined): HelpArticleSummary[] {
  if (!isHelpList(list)) return []
  const seen = new Set<string>()
  const out: HelpArticleSummary[] = []
  for (const section of list.sections) {
    for (const article of Array.isArray(section?.articles) ? section.articles : []) {
      if (!article?.slug || seen.has(article.slug)) continue
      seen.add(article.slug)
      out.push(article)
    }
  }
  return out
}

// ── The "?" button: which article explains this page ──────────────────────

function trimSlash(path: string): string {
  return path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path
}

/** A route matches the path itself, or a path under it (`/orders` matches `/orders/abc`, not `/ordersx`). */
export function routeMatches(route: string, pathname: string): boolean {
  const r = trimSlash(route.trim())
  const path = trimSlash(pathname)
  if (!r) return false
  return path === r || path.startsWith(`${r}/`)
}

/**
 * The visible article whose `routes` holds the longest prefix of the path
 * (`/orders/manage` beats `/orders`). On a tie the article with fewer routes
 * wins (Orders board, one route, before Order details, which lists two; one
 * page's article before the Catalog overview), then the list's own order.
 * Null when nothing matches: the "?" button is hidden then.
 */
export function findArticleForPath(
  articles: HelpArticleSummary[],
  pathname: string,
): HelpArticleSummary | null {
  let best: HelpArticleSummary | null = null
  let bestLength = -1
  let bestRoutes = Infinity
  for (const article of articles) {
    const routes = Array.isArray(article.routes) ? article.routes : []
    for (const route of routes) {
      if (typeof route !== 'string' || !routeMatches(route, pathname)) continue
      const length = trimSlash(route.trim()).length
      if (length > bestLength || (length === bestLength && routes.length < bestRoutes)) {
        best = article
        bestLength = length
        bestRoutes = routes.length
      }
    }
  }
  return best
}

// ── Links inside an article ───────────────────────────────────────────────

/** `help:offerings` → `offerings`; anything that isn't a help link → null. */
export function helpSlugFromHref(href: string | null | undefined): string | null {
  if (!href || !/^help:/i.test(href)) return null
  const slug = href
    .slice('help:'.length)
    .replace(/^\/+/, '')
    .split(/[?#]/)[0]
    .trim()
  return slug && /^[a-z0-9][a-z0-9-]*$/i.test(slug) ? slug : null
}

export function helpPath(slug: string): string {
  return `/help/${slug}`
}

/** The page shows the title itself, so the body's own leading `# Title` is dropped. */
export function stripLeadingTitle(body: string): string {
  return body.replace(/^\s*#[ \t]+[^\n]*\n?/, '')
}

// ── First-run nudge on the Dashboard ──────────────────────────────────────

export function nudgeStorageKey(userId: string | null | undefined, companyId: string | null | undefined): string {
  return `ch_help_first_week_dismissed:${userId ?? 'anon'}:${companyId ?? 'none'}`
}

export function isNudgeDismissed(userId: string | null | undefined, companyId: string | null | undefined): boolean {
  try {
    return localStorage.getItem(nudgeStorageKey(userId, companyId)) === '1'
  } catch {
    return false
  }
}

export function dismissNudge(userId: string | null | undefined, companyId: string | null | undefined): void {
  try {
    localStorage.setItem(nudgeStorageKey(userId, companyId), '1')
  } catch {
    // Private mode or blocked storage: it comes back next visit, nothing breaks.
  }
}
